import { useState, useRef } from 'react';
import { Box, Divider, Button, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import PublicToolLayout from '../../components/public-tools/PublicToolLayout';
import SitemapAnalyzeForm from '../../components/public-tools/SitemapAnalyzeForm';
import type { SitemapFormValues } from '../../components/public-tools/SitemapAnalyzeForm';
import LoadingState from '../../components/public-tools/LoadingState';
import ErrorState from '../../components/public-tools/ErrorState';
import ScanResultSummary from '../../components/public-tools/ScanResultSummary';
import CheckCategoryAccordion from '../../components/public-tools/CheckCategoryAccordion';
import EmailReportCapture from '../../components/public-tools/EmailReportCapture';
import { analyzeUrl, extractErrorMessages } from '../../api/brandingApi';
import type { BrandingAnalyzeResponse } from '../../api/brandingApi';

type WidgetMode = 'idle' | 'loading' | 'result' | 'error';

export default function SitemapValidatorPage() {
  const [mode, setMode]     = useState<WidgetMode>('idle');
  const [result, setResult] = useState<BrandingAnalyzeResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const handleAnalyze = async (values: SitemapFormValues) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setMode('loading');
    setResult(null);
    setErrorMsg('');
    try {
      const data = await analyzeUrl(
        { url: values.url, scanType: 'SITEMAP', sitemapUrl: values.sitemapUrl, xmlContent: values.xmlContent },
        abortRef.current.signal,
      );
      setResult(data);
      setMode('result');
    } catch (err) {
      const msg = extractErrorMessages(err);
      if (msg === '') return;
      setErrorMsg(msg);
      setMode('error');
    }
  };

  const handleScanAgain = () => {
    abortRef.current?.abort();
    setMode('idle');
    setResult(null);
    setErrorMsg('');
  };

  return (
    <PublicToolLayout title="Sitemap Validator">
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Sitemap Validator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your website URL and we'll discover and validate your sitemap automatically.
        Use Advanced options to provide a direct sitemap URL or upload a sitemap XML file.
      </Typography>

      {(mode === 'idle' || mode === 'result') && (
        <Box sx={{ mb: 3 }}>
          <SitemapAnalyzeForm
            loading={mode === 'loading'}
            onSubmit={handleAnalyze}
          />
        </Box>
      )}

      {mode === 'loading' && <LoadingState />}

      {mode === 'error' && (
        <ErrorState message={errorMsg} onRetry={handleScanAgain} />
      )}

      {mode === 'result' && result && (
        <Box>
          <ScanResultSummary result={result} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Check details ({result.checks.length} checks)
          </Typography>
          <CheckCategoryAccordion checks={result.checks} />
          <Box sx={{ mt: 2 }}>
            <Button startIcon={<Refresh />} variant="outlined" size="small" onClick={handleScanAgain}>
              Scan again
            </Button>
          </Box>
          <EmailReportCapture scanId={result.scanId} domain={result.domain} />
        </Box>
      )}
    </PublicToolLayout>
  );
}
